import { Box, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import logo100 from "../../assets/images/logo/hari-logo-100.webp";
import logo200 from "../../assets/images/logo/hari-logo-200.webp";
import logo300 from "../../assets/images/logo/hari-logo-300.webp";
import { tokens } from "../../styles/Theme/Theme";

// How the logo shrinks once the page scrolls: with transforms only, driven by
// `data-scrolled` on the <nav> (see Navbar.jsx).
//
// It used to animate the image's height (100 -> 52px) and the name's font-size
// (22 -> 16px) inside a nav that was in the page flow, so crossing 20px of
// scroll re-laid-out the logo, the nav and then the entire page below it: a
// ~60px jump in the middle of a scroll. Now both keep their resting layout
// size and are scaled and moved into the compact bar instead. The numbers below
// are the difference between the two old layouts, per breakpoint:
//
//   image  resting (padding-left, top, size) -> scrolled, scaled from top-left
//   name   resting left edge / centre line   -> scrolled, scaled from its left
//
// xs:  image 48,11 @40 -> 16,13 @36    name 96 -> 60, same line, same size
// md:  image 104,16 @100 -> 32,8 @52   name 216/66 -> 96/34, 22px -> 16px
// lg:  image 112,16 @100 -> 64,8 @52   name 224/66 -> 128/34, 22px -> 16px
const SCROLLED = "[data-scrolled='true'] &";
// The name's colour follows the tone Navbar.jsx samples from the section under
// the bar, also as an attribute on the <nav>: dark text by default, light text
// over dark or gradient sections.
const DARK = "[data-tone='dark'] &";
const EASE = `transform ${tokens.transitions.normal}`;

const NavbarLogo = () => {
  return (
    <Box
      component={Link}
      to="/"
      sx={{
        display: "flex",
        alignItems: "center",
        textDecoration: "none",
        cursor: "pointer",
        pointerEvents: "auto",
      }}
    >
      <Box
        component="img"
        src={logo200}
        srcSet={`${logo100} 1x, ${logo200} 2x, ${logo300} 3x`}
        width={100}
        height={100}
        decoding="async"
        alt="Hari Logo"
        sx={{
          display: "block",
          // Mobile shrunk so the logo nests inside the small white blob (legacy look).
          width: { xs: 40, md: 100 },
          height: { xs: 40, md: 100 },
          transformOrigin: "0 0",
          transition: EASE,
          [SCROLLED]: {
            transform: {
              xs: "translate(-32px, 2px) scale(0.9)",
              md: "translate(-72px, -8px) scale(0.52)",
              lg: "translate(-48px, -8px) scale(0.52)",
            },
          },
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      />

      <Box
        sx={{
          ml: { xs: 1, md: 1.5 },
          fontFamily: tokens.typography.fontFamily.heading,
          color: tokens.colors.background.dark,
          [DARK]: { color: tokens.colors.text.primary },
          fontSize: { xs: "13px", md: "22px" },
          whiteSpace: "nowrap",
          transformOrigin: "0 50%",
          transition: `${EASE}, color ${tokens.transitions.normal}`,
          [SCROLLED]: {
            transform: {
              xs: "translate(-36px, 0)",
              md: "translate(-120px, -32px) scale(0.727)",
              lg: "translate(-96px, -32px) scale(0.727)",
            },
          },
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: "inherit",
            fontFamily: "inherit",
            color: "inherit",
          }}
        >
          <Box component="span" sx={{ fontWeight: 700 }}>
            haribabu
          </Box>{" "}
          <Box
            component="span"
            sx={{
              fontWeight: 400,
              opacity: 0.85,
            }}
          >
            Manoharan
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

export default NavbarLogo;
